import * as React from "react";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";
import moment from "moment";
import { Link } from "react-router-dom";

import { Button, ErrorMessage, ExchangeNotificationSnackBar } from "components";
import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import { BaseContainer } from "containers";
import { Desktop, Mobile } from "containers/Responsive";
import { formatCurrencyAmount } from "lib/format";
import * as routes from "routes";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import { useExchangeNotifications } from "services/exchangeNotifications";
import { useOffer } from "services/offers";
import {
  DUE_DILIGENCE_STATE,
  OFFER_STATE,
  PAYMENT_STATE,
  TRANSFER_STATE,
} from "services/offers/constants";
import { linkPurpleStyles } from "theme/constants";

import DealProcessSteps from "./components/DealProcessSteps";
import InfoBox from "./components/InfoBox";
import { NextStepStatus } from "./components/NextStepBox";

const useStyles = makeStyles(theme => ({
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
  mainContainer: {
    marginBottom: theme.spacing(5),
    filter: "none",
  },
  pageTitle: {
    marginBottom: theme.spacing(4.5),
    fontSize: "3rem",
    fontWeight: 300,
    lineHeight: "3.625rem",
    color: theme.palette.text.primary,
    textTransform: "none",
  },
  dealInfoContainer: {
    display: "flex",
    marginBottom: theme.spacing(6.25),
  },
  exitButton: {
    paddingLeft: 0,
    textTransform: "capitalize",
    "&:hover": {
      color: theme.custom.palette.black.main,
    },
  },
  backButtonInner: linkPurpleStyles,
}));

const stepStatus = (
  inProgressState: string,
  failState: readonly string[],
  offerState: string,
): NextStepStatus => {
  if (inProgressState === offerState) {
    return "pending";
  }

  if (failState.includes(offerState)) {
    return "failed";
  }
  return "ok";
};

const DealPage: React.FC = () => {
  const classes = useStyles();
  const url = new URL(window.location.toLocaleString());
  const offerId = parseInt(url.searchParams.get("offerId"), 10);

  const { data: dealOffer, isLoading, error } = useOffer(offerId);

  const { data: exchangeNotification } = useExchangeNotifications(
    {
      filter: { kind: ["deal_page"] },
    },
    true, // this is an authentication required view
  );
  const { amount, state, listing_id, term_expiration_date } = dealOffer || {};

  const earnsThrough = `${
    term_expiration_date
      ? moment.utc(term_expiration_date).format("MMMM D, YYYY")
      : EMPTY_VALUE_PLACEHOLDER
  }`;

  const dueDiligenceStep = OFFER_STATE.includes(state)
    ? stepStatus(DUE_DILIGENCE_STATE.pending, DUE_DILIGENCE_STATE.fail, state)
    : "empty";

  const paymentStep =
    dueDiligenceStep === "ok"
      ? stepStatus(PAYMENT_STATE.pending, PAYMENT_STATE.fail, state)
      : "empty";

  const transferStep =
    paymentStep === "ok"
      ? stepStatus(TRANSFER_STATE.pending, TRANSFER_STATE.fail, state)
      : "empty";

  const transactionCompleteStep =
    transferStep === "ok" && "filled" === state ? "ok" : "empty";

  return (
    <>
      <BaseContainer>
        {exchangeNotification && (
          <ExchangeNotificationSnackBar
            key={exchangeNotification?.title}
            title={exchangeNotification.title}
            message={exchangeNotification.message}
          />
        )}
        {isLoading && (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={30} />
          </Box>
        )}
        {error && (
          <Box my={2}>
            <ErrorMessage
              title="Request Failed"
              description={DEFAULT_ERROR_MESSAGE}
            />
          </Box>
        )}
        {dealOffer && (
          <>
            <Box mt={2} mb={3} display="flex" justifyContent="flex-start">
              <Button
                className={classes.exitButton}
                variant="text"
                startIcon={<ArrowBackIcon />}
                size="large"
                component={Link}
                to={routes.orderBookAssetDetail(listing_id.toString())}
              >
                Back to Asset Detail
              </Button>
            </Box>
            <Box className={classes.mainContainer}>
              <Typography variant="h4" className={classes.pageTitle}>
                Deal Made!
              </Typography>
              <Desktop>
                <Box className={classes.dealInfoContainer}>
                  <InfoBox
                    title="Amount"
                    info={`${formatCurrencyAmount("USD", amount)}`}
                  />
                  <InfoBox title="Earns Through" info={earnsThrough} />
                </Box>
              </Desktop>
              <Mobile>
                <InfoBox
                  title="Amount"
                  info={`${formatCurrencyAmount("USD", amount)}`}
                />
                <InfoBox title="Earns Through" info={earnsThrough} />
              </Mobile>
              <Typography variant="h4" className={classes.pageTitle}>
                Closing Process
              </Typography>
              <DealProcessSteps
                dueDiligenceStep={dueDiligenceStep}
                paymentStep={paymentStep}
                transferStep={transferStep}
                transactionCompleteStep={transactionCompleteStep}
              />
            </Box>
          </>
        )}
      </BaseContainer>
    </>
  );
};

export default DealPage;
